import React, { useCallback, useContext, useEffect, useState } from 'react'
import debounce from 'lodash.debounce'

import CssBaseline from '@material-ui/core/CssBaseline'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Container from '@material-ui/core/Container'
import { UsernameMessage } from '../username-message'
import { makeStyles, Button } from '@material-ui/core'
import { CustomDialogContent, Loading } from '..'
import { useDispatch, useSelector } from 'react-redux'
import { openSnackbar } from '../../ducks/snackbar/actions'
import {
  addMemberToCircle,
  getDocumentFromFirestore,
  ICircle,
  IUsername,
  IUser,
} from 'daily-dose-common'
import { firestore, functions } from '../../firebase-init';
import {
  asyncError,
  asyncFinish,
  asyncStart,
  clearState,
} from '../../ducks/ui/actions'
import { IStoreState } from '../../store/types'
import { FirestoreContext } from '../../providers/firestore-provider'
import * as Utilities from '../../utilities'

// TODO: TextField aren ot longer supported according to Google Material Design so this needs to be refactor
// https://medium.com/google-design/the-evolution-of-material-designs-text-fields-603688b3fe03

//TODO: Implement forget password

const useStyles = makeStyles((theme) => ({
  submit: {
    margin: theme.spacing(2, 0, 2),
  },
}))

interface IAddMember {
  selectedCircle: ICircle
  circleId: string
  onAddMember: () => void
  onCancel: () => void
}

const AddMember = ({
  selectedCircle,
  circleId,
  onAddMember,
  onCancel,
}: IAddMember) => {
  const { currentUser } = useContext(FirestoreContext)
  const classes = useStyles()
  const dispatch = useDispatch()
  const ui = useSelector((state: IStoreState) => state.ui)
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [isValid, setIsValid] = useState(false)
  // const [loading, setLoading] = useState(false)
  // const [errorMessage, setErrorMessage] = useState('')
  const [isValidEmail, setIsValidEmail] = useState(false)

  useEffect(() => {
    checkUsername(username)
    // eslint-disable-next-line
  }, [username])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const circleRef = firestore.collection('circles').doc(circleId)
    try {
      dispatch(asyncStart('MEMBER-ADD'))
      const usernameRef = firestore.collection('usernames').doc(username)
      const usernameData = (await getDocumentFromFirestore(usernameRef)) as IUsername
      const newMemberRef = firestore.collection('users').doc(usernameData.id)
      const newMemberData = (await getDocumentFromFirestore(newMemberRef)) as IUser

      if (newMemberData) {
        await addMemberToCircle(newMemberData, circleRef)
        setUsername('')
        setIsValid(false)
        dispatch(asyncFinish('MEMBER-ADDED'))
        dispatch(
          openSnackbar(
            `${username} has been invited. Time to dose together 😍`,
            'success'
          )
        )
        onAddMember()
      }
    } catch (error) {
      console.warn('Error adding member ', error)
      dispatch(asyncError(error))
    }
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Force form value typed in form to match correct format
    const val = e.target.value.toLowerCase()
    const re = /^(?=[a-zA-Z0-9._]{3,15}$)(?!.*[_.]{2})[^_.].*[^_.]$/

    // Only set form value if length is < 3 OR it passes regex
    if (val.length < 3) {
      setUsername(val)
      dispatch(clearState())
      setIsValid(false)
    }

    if (re.test(val)) {
      setUsername(val)
      dispatch(asyncStart('USERNAME-CHECKING'))
      setIsValid(false)
    }
  }

  const handleSubmitEmail = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    try {
      if (isValidEmail) {
        // const invitationsRef = firestore.collection('invitations')
        const data = {
          email,
          inviterUsername: currentUser?.username,
          inviterName: currentUser?.displayName,
          circle: selectedCircle,
        }
        setUsername('')
        dispatch(asyncStart('MEMBER-INVITE'))

        // Send invitation by email
        const inviteUserByEmail = functions.httpsCallable('inviteUserByEmail')
        await inviteUserByEmail(data)

        dispatch(asyncFinish('MEMBER-INVITED'))
        dispatch(
          openSnackbar(
            `Awesome! An email invitation was sent to ${email}`,
            'success',
            7000
          )
        )

        onAddMember()
      }
    } catch (error) {
      dispatch(asyncError(error))
      if (error.code === 'already-exists') {
        dispatch(openSnackbar(error.message, 'warning', 11000))
        console.error(`Error: ${error.message}`)
      } else {
        dispatch(openSnackbar(`Opps. Could not invite ${email}. Try again`, 'error'))
        console.error(`Error: Could not invite  ${email}. Reason: ${error}.`)
      }
    }
  }

  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Force form to be an email
    const val = e.target.value.toLowerCase()
    setEmail(val)

    if (Utilities.isValidEmail(val)) {
      setIsValidEmail(true)
    } else {
      setIsValidEmail(false)
    }
  }

  // Hit the database for username match after each debounced change
  // useCallback is required for debounce to work
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkUsername = useCallback(
    debounce(async (username: string) => {
      if (!username) return

      if (selectedCircle.membersUsernames.includes(username)) {
        setIsValid(false)
        dispatch(asyncError(`${username.toUpperCase()} is already in this group`))
        return
      }
      if (username.length >= 3) {
        const ref = firestore.doc(`usernames/${username}`)
        const { exists } = await ref.get()
        // check that user exists
        if (exists) {
          setIsValid(exists)
          dispatch(asyncFinish('USERNAME-FOUND'))
        }
        // TODO User does not exist, create invitation
        else {
          console.warn('User does not exist. Create invitacion')
          setIsValid(false)

          dispatch(
            asyncError(
              `We couldn't find a member with username ${username.toUpperCase()}. Invite a new member with an email `
            )
          )
        }
      }
    }, 500),
    [selectedCircle]
  )

  return (
    <CustomDialogContent
      title="Add Member"
      confirmText="Add"
      onCancel={onCancel}
      onSubmit={handleSubmit}
    >
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        {ui.loading && !username && <Loading />}
        <div>
          <Typography variant="h6">
            Does the member already exist in the DD?
          </Typography>
          <Typography variant="body1">
            Find a user by its username or send an invitation by email
          </Typography>
          <form noValidate onSubmit={handleSubmit}>
            <TextField
              value={username}
              onChange={onChange}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="off"
              autoFocus
            />
            <UsernameMessage
              username={username}
              isValid={isValid}
              loading={ui.loading && ui.message === 'USERNAME-CHECKING'}
              successMessage={`Invite ${username} to ${selectedCircle.name}`}
              errorMessage={ui.error}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={!isValid}
              className={classes.submit}
            >
              Invite Doser
            </Button>
          </form>
          <form noValidate onSubmit={handleSubmitEmail}>
            <TextField
              value={email}
              onChange={onChangeEmail}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="off"
              autoFocus
              type="email"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={
                !isValidEmail || (ui.loading && ui.message === 'MEMBER-INVITE')
              }
              className={classes.submit}
            >
              Send invitacion by email
            </Button>
          </form>
        </div>
      </Container>
    </CustomDialogContent>
  )
}

export default AddMember
